import { ReactNode, useState } from "react";
import { Collapse } from "react-collapse";

import ChevronDownIcon from "@assets/icons/chevron-down.svg";

import { cn } from "@utils/tailwind.util";

interface CollapsibleSectionProps {
  title: string;
  children: ReactNode;
  // Collapsed unless told otherwise. Ignored once `isOpen` is passed.
  defaultOpen?: boolean;
  // Pass both to control the section from the parent, e.g. to expand it over a hidden error.
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;
}

// A titled section that folds away: a tinted header on the outer border, and the content in its
// own card inside it.
export function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
  isOpen: controlledIsOpen,
  onOpenChange,
  className,
}: CollapsibleSectionProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;

  const toggle = () => {
    setUncontrolledIsOpen(!isOpen);
    onOpenChange?.(!isOpen);
  };

  return (
    <div
      className={cn(
        "rounded-xl bg-background-secondary",
        "dark:border-gray-600 dark:bg-gray-750",
        className
      )}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-2 px-4 py-3"
        onClick={toggle}
      >
        <span className="text-sm font-medium text-content-secondary dark:text-invertedPrimary80">
          {title}
        </span>
        <ChevronDownIcon
          width={20}
          height={20}
          className={cn(
            "flex-shrink-0 text-content-secondary transition-transform duration-300 dark:text-invertedPrimary80",
            isOpen ? "rotate-0" : "-rotate-90"
          )}
        />
      </button>

      <Collapse isOpened={isOpen}>
        <div className="p-0.5 pt-0">
          <div className="rounded-lg border border-stroke-primary bg-background-primary p-3 dark:border-gray-600 dark:bg-gray-800">
            {children}
          </div>
        </div>
      </Collapse>
    </div>
  );
}
